import { useState } from 'react';
import {
  DataGridPro, GridActionsCellItem, GridColDef, GridRowModel, GridRowModes, useGridApiRef,
} from '@mui/x-data-grid-pro';
import SvgIcon from '@mui/material/SvgIcon';
import CancelIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import Card from '@mui/material/Card';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { TIntegrationExportTable } from '../../type';
import { IntegrationHeader } from './IntegrationHeader';
import { IntegrationFooter } from './IntegrationFooter';
import { TMember } from '../../../../types';
import { useEditTable } from '../../../../hooks/useTable';
import { ReactComponent as SaveIcon } from '../../../../static/icons/ic_save.svg';

export function IntegrationExportTable<T extends {
  id: number | string,
  isNew: boolean,
  email: string }>({
  getReport,
  isLoading,
  columns,
  integrationName,
  rowsData,
  setDates,
  dates,
}: TIntegrationExportTable<T>) {
  const { t } = useTranslation('common');
  const { pageHeight, resizableDiv } = useScreenResize(80);
  const apiRef = useGridApiRef();
  const {
    handleRowEditStop,
    rowModesModel,
    handleSaveClick,
    handleCancelClick,
    handleRowModesModelChange,
    rows,
    setRows,
  } = useEditTable(rowsData, true);

  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);

  const [selectedMembers, setSelectedMembers] = useState<Array<TMember>>([]);

  const baseColumns: GridColDef = {
    field: 'actions',
    type: 'actions',
    headerName: t('actions'),
    minWidth: 100,
    cellClassName: 'actions',
    disableExport: true,
    getActions: (params) => {
      const { id } = params;
      const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

      if (isInEditMode) {
        return [
          <GridActionsCellItem
            key={id}
            icon={(
              <SvgIcon
                color="primary"
                fontSize="small"
                component={SaveIcon}
                inheritViewBox
              />
              )}
            label="Save"
            onClick={handleSaveClick(id)}
          />,
          <GridActionsCellItem
            key={id}
            icon={<CancelIcon />}
            label="Cancel"
            className="textPrimary"
            onClick={handleCancelClick(id)}
            color="inherit"
          />,
        ];
      }

      return [];
    },
  };

  const handleSubmit = () => {
    const [from, to] = dates;
    apiRef.current?.exportDataAsCsv({
      fileName: `MGM-${integrationName}-${from.format('YYYY-MM-DD')}-to-${to.format('YYYY-MM-DD')}`,
    });
  };

  const handleReport = () => {
    const emails = selectedMembers.map(({ email }) => email);
    getReport(emails, dates);
  };

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('itLooksLikeThereAreNoData')} />;

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false } as typeof rowsData[0];
    setRows(rows?.map((row) => (row.email === newRow.email ? updatedRow : row)));
    return updatedRow;
  };

  return (
    <div>
      <IntegrationHeader
        isFetching={isLoading}
        onClick={handleReport}
        setDatesRange={setDates}
        setSelectedMembers={setSelectedMembers}
        selectedMembers={selectedMembers}
        isExportBtnDisabled={!rows.length}
        handleSubmit={handleSubmit}
      />
      <div ref={resizableDiv}>
        <Card
          className="overflow-auto"
          sx={{
            minHeight: 450,
            height: pageHeight !== 0 ? pageHeight : 'auto',
            ...(isMobile && {
              background: 'unset', boxShadow: 'unset', px: 0, pt: 0, pb: '35px',
            }),
          }}
        >
          <DataGridPro
            apiRef={apiRef}
            rows={rows}
            columns={[...columns, baseColumns] as Array<GridColDef>}
            rowHeight={50}
            editMode="row"
            hideFooter
            getRowId={(row) => row.email}
            rowModesModel={rowModesModel}
            loading={isLoading}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            slots={{
              noRowsOverlay: renderCustomNoRowsOverlay,
            }}
          />
        </Card>
        {!isMobile && (
        <IntegrationFooter
          isNextBtnDisabled={!rows.length}
          nextBtnText="export"
          isLoading={false}
          integrationName={integrationName}
          handleSubmit={handleSubmit}
        />
        )}
      </div>
    </div>
  );
}
